import React, { useState } from "react";
import {Box, Tooltip} from "@mui/material";
import HelpOutlinedIcon from "@mui/icons-material/HelpOutlined";
import {getIconForNationality} from "crqlar-react-common/dist/utils/translations.util";

// Mapping of country codes to their dominant native locales
const COUNTRY_NATIVE_LOCALES: Record<string, string> = {
    AD: "ca", // Andorra -> Catalan
    AE: "ar", // UAE -> Arabic
    AF: "ps", // Afghanistan -> Pashto
    AL: "sq", // Albania -> Albanian
    AM: "hy", // Armenia -> Armenian
    AO: "pt", // Angola -> Portuguese
    AR: "es", // Argentina -> Spanish
    AT: "de", // Austria -> German
    AU: "en", // Australia -> English
    AZ: "az", // Azerbaijan -> Azerbaijani
    BA: "bs", // Bosnia -> Bosnian
    BD: "bn", // Bangladesh -> Bengali
    BE: "nl", // Belgium -> Dutch/French
    BG: "bg", // Bulgaria -> Bulgarian
    BH: "ar", // Bahrain -> Arabic
    BI: "rn", // Burundi -> Kirundi
    BJ: "fr", // Benin -> French
    BN: "ms", // Brunei -> Malay
    BO: "es", // Bolivia -> Spanish
    BR: "pt", // Brazil -> Portuguese
    BS: "en", // Bahamas -> English
    BT: "dz", // Bhutan -> Dzongkha
    BY: "be", // Belarus -> Belarusian
    CA: "en", // Canada -> English/French
    CH: "de", // Switzerland -> German/French/Italian
    CL: "es", // Chile -> Spanish
    CM: "fr", // Cameroon -> French/English
    CN: "zh", // China -> Chinese
    CO: "es", // Colombia -> Spanish
    CR: "es", // Costa Rica -> Spanish
    CU: "es", // Cuba -> Spanish
    CY: "el", // Cyprus -> Greek
    CZ: "cs", // Czechia -> Czech
    DE: "de", // Germany -> German
    DK: "da", // Denmark -> Danish
    DO: "es", // Dominican Republic -> Spanish
    DZ: "ar", // Algeria -> Arabic
    EC: "es", // Ecuador -> Spanish
    EE: "et", // Estonia -> Estonian
    EG: "ar", // Egypt -> Arabic
    ES: "es", // Spain -> Spanish
    ET: "am", // Ethiopia -> Amharic
    FI: "fi", // Finland -> Finnish
    FR: "fr", // France -> French
    GB: "en", // UK -> English
    GE: "ka", // Georgia -> Georgian
    GH: "en", // Ghana -> English
    GR: "el", // Greece -> Greek
    GT: "es", // Guatemala -> Spanish
    HN: "es", // Honduras -> Spanish
    HR: "hr", // Croatia -> Croatian
    HU: "hu", // Hungary -> Hungarian
    ID: "id", // Indonesia -> Indonesian
    IE: "ga", // Ireland -> Irish/English
    IL: "he", // Israel -> Hebrew
    IN: "hi", // India -> Hindi
    IQ: "ar", // Iraq -> Arabic
    IR: "fa", // Iran -> Persian
    IS: "is", // Iceland -> Icelandic
    IT: "it", // Italy -> Italian
    JM: "en", // Jamaica -> English
    JO: "ar", // Jordan -> Arabic
    JP: "ja", // Japan -> Japanese
    KE: "sw", // Kenya -> Swahili
    KG: "ky", // Kyrgyzstan -> Kyrgyz
    KH: "km", // Cambodia -> Khmer
    KP: "ko", // North Korea -> Korean
    KR: "ko", // South Korea -> Korean
    KW: "ar", // Kuwait -> Arabic
    KZ: "kk", // Kazakhstan -> Kazakh
    LB: "ar", // Lebanon -> Arabic
    LI: "de", // Liechtenstein -> German
    LK: "si", // Sri Lanka -> Sinhala
    LT: "lt", // Lithuania -> Lithuanian
    LU: "lb", // Luxembourg -> Luxembourgish
    LV: "lv", // Latvia -> Latvian
    LY: "ar", // Libya -> Arabic
    MA: "ar", // Morocco -> Arabic
    MC: "fr", // Monaco -> French
    MD: "ro", // Moldova -> Romanian
    ME: "cnr", // Montenegro -> Montenegrin
    MG: "mg", // Madagascar -> Malagasy
    MK: "mk", // Macedonia -> Macedonian
    ML: "fr", // Mali -> French
    MM: "my", // Myanmar -> Burmese
    MN: "mn", // Mongolia -> Mongolian
    MT: "mt", // Malta -> Maltese
    MX: "es", // Mexico -> Spanish
    MY: "ms", // Malaysia -> Malay
    MZ: "pt", // Mozambique -> Portuguese
    NA: "en", // Namibia -> English
    NE: "fr", // Niger -> French
    NG: "en", // Nigeria -> English
    NI: "es", // Nicaragua -> Spanish
    NL: "nl", // Netherlands -> Dutch
    NO: "no", // Norway -> Norwegian
    NP: "ne", // Nepal -> Nepali
    NZ: "en", // New Zealand -> English/Maori
    OM: "ar", // Oman -> Arabic
    PA: "es", // Panama -> Spanish
    PE: "es", // Peru -> Spanish
    PH: "fil", // Philippines -> Filipino
    PK: "ur", // Pakistan -> Urdu
    PL: "pl", // Poland -> Polish
    PT: "pt", // Portugal -> Portuguese
    PY: "es", // Paraguay -> Spanish
    QA: "ar", // Qatar -> Arabic
    RO: "ro", // Romania -> Romanian
    RS: "sr", // Serbia -> Serbian
    RU: "ru", // Russia -> Russian
    RW: "rw", // Rwanda -> Kinyarwanda
    SA: "ar", // Saudi Arabia -> Arabic
    SD: "ar", // Sudan -> Arabic
    SE: "sv", // Sweden -> Swedish
    SG: "en", // Singapore -> English/Malay/Chinese/Tamil
    SI: "sl", // Slovenia -> Slovenian
    SK: "sk", // Slovakia -> Slovak
    SN: "fr", // Senegal -> French
    SO: "so", // Somalia -> Somali
    SR: "nl", // Suriname -> Dutch
    SV: "es", // El Salvador -> Spanish
    SY: "ar", // Syria -> Arabic
    SZ: "ss", // Swaziland -> Swati
    TD: "fr", // Chad -> French/Arabic
    TH: "th", // Thailand -> Thai
    TJ: "tg", // Tajikistan -> Tajik
    TN: "ar", // Tunisia -> Arabic
    TR: "tr", // Turkey -> Turkish
    TT: "en", // Trinidad -> English
    TW: "zh", // Taiwan -> Chinese
    TZ: "sw", // Tanzania -> Swahili
    UA: "uk", // Ukraine -> Ukrainian
    UG: "en", // Uganda -> English/Swahili
    US: "en", // USA -> English
    UY: "es", // Uruguay -> Spanish
    UZ: "uz", // Uzbekistan -> Uzbek
    VA: "it", // Vatican -> Italian
    VE: "es", // Venezuela -> Spanish
    VN: "vi", // Vietnam -> Vietnamese
    YE: "ar", // Yemen -> Arabic
    ZA: "zu", // South Africa -> Zulu/Xhosa/Afrikaans/English
    ZM: "en", // Zambia -> English
    ZW: "en", // Zimbabwe -> English
};

// Always renders "NativeName (EnglishName)" for country codes
// to match the exact behavior of getNameForLanguage in the LanguagesElement.
const getCountryName = (nationality: string): string => {
    try {
        const code = nationality.toUpperCase();
        const nativeLocale = COUNTRY_NATIVE_LOCALES[code] || "en";
        const nativeName = new Intl.DisplayNames([nativeLocale], {type: "region"}).of(code) || nationality;
        const englishName = new Intl.DisplayNames(["en"], {type: "region"}).of(code) || nationality;

        if (nativeName && englishName && nativeName !== englishName) {
            return `${nativeName} (${englishName})`;
        }
        return nativeName || englishName || nationality;
    } catch {
        return nationality;
    }
};

export function CountryCodeElement({countryCode, asPrint = false, shape = "round"}: {
    countryCode: string | undefined;
    asPrint?: boolean;
    shape?: "round" | "flag";
}) {
    const [hasError, setHasError] = useState(false);

    if (!countryCode) {
        return null;
    }

    const label = getCountryName(countryCode);

    if (asPrint) {
        return <>{countryCode.toUpperCase()}</>;
    }

    const countryIcon = getIconForNationality(countryCode);

    if (!countryIcon || hasError) {
        return (
            <Tooltip title={label} arrow>
                <HelpOutlinedIcon sx={{display: "block", fontSize: 16, color: "text.secondary"}}/>
            </Tooltip>
        );
    }

    return (
        <Tooltip title={label} arrow>
            <Box
                component="img"
                alt={label}
                src={countryIcon}
                onError={() => setHasError(true)}
                sx={{
                    display: "block",
                    width: 18,
                    height: shape === "flag" ? 12 : 18,
                    borderRadius: shape === "flag" ? 0.5 : "50%",
                    objectFit: "cover",
                    border: shape === "flag" ? "0.5px solid" : undefined,
                    borderColor: shape === "flag" ? "divider" : undefined,
                    flexShrink: 0,
                }}
            />
        </Tooltip>
    );
}

export function NationalityElement({nationality, asPrint = false}: {
    nationality: string | undefined;
    asPrint?: boolean;
}) {
    return <CountryCodeElement countryCode={nationality} asPrint={asPrint}/>;
}
